import { Asset, AssetType, TenantUserSettings } from '@/api/generatedApi';
import { useToast } from '@/hooks/useToast';
import { useSessionState } from '@/hooks/useSessionState';
import { ReactNode } from 'react';
import { CompareCartContext, CompareCartState } from './CompareCartContext';
import { MatchData } from '@/types/matchData';

const SESSION_STORAGE_KEY = 'physnaCompareCart';

const isComparableAssetType = (asset?: Asset): boolean =>
  asset?.type === AssetType.Model || asset?.type === AssetType.Scan;

export interface AssetWithMatchData extends Asset {
  matchData?: MatchData;
}

export const CompareCartProvider = ({ children }: { children: ReactNode }) => {
  const { showToast } = useToast();

  const [selectedForCompare, setSelectedForCompareState] = useSessionState<{ asset?: AssetWithMatchData }>(
    SESSION_STORAGE_KEY,
    {}
  );

  const setSelectedForCompare = (selection: { asset?: AssetWithMatchData }) => {
    if (selection.asset && !isComparableAssetType(selection.asset)) {
      showToast({
        message: 'Only model or scan assets can be selected for compare',
        type: 'warning',
      });
      return;
    }
    setSelectedForCompareState(selection);
  };

  const isSelected = (assetId: string) => {
    return selectedForCompare.asset?.id === assetId;
  };

  const clearCart = () => {
    setSelectedForCompareState({});
  };

  const getCompareUrl = (asset1: Asset, asset2: Asset, userSettings?: TenantUserSettings[], matchData?: MatchData) => {
    const compareParameters = new URLSearchParams({
      asset1Id: asset1.id,
      asset2Id: asset2.id,
      tenant1Id: asset1.tenantId,
      tenant2Id: asset2.tenantId,
    });

    let effectiveMatchData = matchData;
    if (!effectiveMatchData && selectedForCompare.asset) {
      const selectedAsset = selectedForCompare.asset;
      if (selectedAsset.id === asset2.id && selectedAsset.matchData?.searchAssetId === asset1.id) {
        effectiveMatchData = selectedAsset.matchData;
      } else if (selectedAsset.id === asset1.id && selectedAsset.matchData?.searchAssetId === asset2.id) {
        const originalMatchData = selectedAsset.matchData;
        effectiveMatchData = {
          searchType: originalMatchData.searchType,
          matchPercentage: originalMatchData.matchPercentage,
          forwardMatchPercentage: originalMatchData.reverseMatchPercentage,
          reverseMatchPercentage: originalMatchData.forwardMatchPercentage,
        };
      }
    }

    if (effectiveMatchData?.searchType) {
      compareParameters.set('searchType', effectiveMatchData.searchType);
    }
    if (effectiveMatchData?.matchPercentage !== undefined) {
      compareParameters.set('matchPercentage', effectiveMatchData.matchPercentage.toString());
    }
    if (effectiveMatchData?.forwardMatchPercentage !== undefined) {
      compareParameters.set('forwardMatchPercentage', effectiveMatchData.forwardMatchPercentage.toString());
    }
    if (effectiveMatchData?.reverseMatchPercentage !== undefined) {
      compareParameters.set('reverseMatchPercentage', effectiveMatchData.reverseMatchPercentage.toString());
    }

    const tenant1ShortName =
      userSettings?.find(tenant => tenant.tenantId === asset1.tenantId && tenant.userEnabled)?.tenantShortName ||
      asset1.tenantId;

    return `/tenants/${tenant1ShortName}/compare?${compareParameters}`;
  };

  const value: CompareCartState = {
    selectedForCompare,
    setSelectedForCompare,
    isSelected,
    clearCart,
    getCompareUrl,
  };

  return <CompareCartContext.Provider value={value}>{children}</CompareCartContext.Provider>;
};
